import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from "../../firebase"; 
import { collection, addDoc, getDocs, getCountFromServer } from 'firebase/firestore'; // Import getCountFromServer
import './AddGroceryProduct.css';

function AddGroceryProduct() {
  const navigate = useNavigate();
  
  // State for core product information
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // State for multiple variants, each with its own properties
  const [variants, setVariants] = useState([{ unit: '', price: '', quantity: '', discount: '' }]);
  
  // State for storing multiple image Base64 strings
  const [imageBase64Strings, setImageBase64Strings] = useState([]);

  // State for categories fetched from DB and UI states
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch categories from Firestore when the component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'Category grocery'));
        const categoriesList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setCategories(categoriesList);
      } catch (err) {
        console.error("Error fetching categories: ", err);
        setError('Failed to load categories. Please check your connection and Firestore rules.');
      }
    };
    fetchCategories();
  }, []);

  // --- IMAGE HANDLING LOGIC ---
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > 750 * 1024) { // 750KB size limit
        return reject(new Error(`Image "${file.name}" is too large (max 750KB).`));
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setError(''); // Clear previous image-related errors
    
    try {
      const base64Promises = files.map(file => convertFileToBase64(file));
      const newBase64Strings = await Promise.all(base64Promises);
      setImageBase64Strings(prev => [...prev, ...newBase64Strings]);
    } catch (err) {
      console.error("Error converting images:", err);
      setError(err.message); // Display specific error (e.g., file size)
    }
  };

  const removeImage = (index) => {
    setImageBase64Strings(current => current.filter((_, i) => i !== index));
  };

  // --- VARIANT HANDLING LOGIC ---
  const handleVariantChange = (index, event) => {
    const values = [...variants];
    values[index][event.target.name] = event.target.value;
    setVariants(values);
  };
  
  const addVariant = () => {
    setVariants([...variants, { unit: '', price: '', quantity: '', discount: '' }]);
  };

  const removeVariant = (index) => {
    if (variants.length > 1) { // Prevent removing the last variant
      const values = [...variants];
      values.splice(index, 1);
      setVariants(values);
    }
  };

  // --- FORM SUBMISSION LOGIC ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Comprehensive validation
    if (!name || !selectedCategory) {
      setError('Please fill out the Product Name and select a Category.');
      return;
    }
    if (imageBase64Strings.length === 0) {
      setError('Please upload at least one product image.');
      return;
    }
    const isAnyVariantIncomplete = variants.some(v => !v.unit || !v.price || !v.quantity);
    if (isAnyVariantIncomplete) {
      setError('Please ensure all variant fields (Unit, Price, and Quantity) are filled out.');
      return;
    }

    setLoading(true);

    try {
      // 1. Get the count of existing products to generate the new ID
      const productsCollectionRef = collection(db, 'groceryProducts');
      const snapshot = await getCountFromServer(productsCollectionRef);
      const productCount = snapshot.data().count;
      
      // 2. Format the new product ID
      const newProductId = `PRO-${(productCount + 1).toString().padStart(4, '0')}`;

      // 3. Create the product object with the new productId and the correct data structure
      await addDoc(productsCollectionRef, {
        productId: newProductId, // Add the generated product ID
        name: name,
        description: description,
        category: selectedCategory,
        images_base64: imageBase64Strings,
        variants: variants.map(v => ({
          unit: v.unit,
          price: parseFloat(v.price),
          quantity: parseInt(v.quantity, 10),
          discount: v.discount ? parseFloat(v.discount) : 0,
        })),
        createdAt: new Date(),
      });

      alert('Product added successfully!');
      navigate('/groceryproduct'); // Navigate to your product list page
    } catch (err) {
      console.error("Error adding document: ", err);
      setError('Failed to save the product. Data may be too large or there was a network issue.');
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="container">
      <h2>Add New Grocery Product</h2>
      <form onSubmit={handleSubmit} className="product-form">
        {error && <p className="error-message">{error}</p>}
        
        <div className="form-group">
          <label htmlFor="name">Product Name</label>
          <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        
        <div className="form-group">
          <label htmlFor="category">Category</label>
          <select id="category" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} required>
            <option value="" disabled>Select a category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="description">Description (Optional)</label>
          <textarea id="description" rows="4" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="form-group">
          <label htmlFor="image">Product Images (up to 750KB each)</label>
          <input type="file" id="image" multiple accept="image/*" onChange={handleImageChange} className="file-input" />
        </div>

        <div className="image-previews">
          {imageBase64Strings.map((base64, index) => (
            <div key={index} className="image-preview-item">
              <img src={base64} alt={`preview-${index}`} />
              <button type="button" onClick={() => removeImage(index)} className="remove-image-button">×</button>
            </div>
          ))}
        </div>

        <hr />
        <h3>Product Variants</h3>
        {variants.map((variant, index) => (
          <div key={index} className="variant-group">
            <div className="form-group">
              <label>Unit</label>
              <input type="text" name="unit" placeholder="e.g., 5 kg" value={variant.unit} onChange={e => handleVariantChange(index, e)} required />
            </div>
            <div className="form-group">
              <label>Price (₹)</label>
              <input type="number" name="price" placeholder="e.g., 250" value={variant.price} onChange={e => handleVariantChange(index, e)} required />
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" name="quantity" placeholder="e.g., 100" value={variant.quantity} onChange={e => handleVariantChange(index, e)} required />
            </div>
            <div className="form-group">
              <label>Discount (%)</label>
              <input type="number" name="discount" placeholder="e.g., 10" value={variant.discount} onChange={e => handleVariantChange(index, e)} />
            </div>
            {variants.length > 1 && (
              <button type="button" className="remove-variant-button" onClick={() => removeVariant(index)}>Remove</button>
            )}
          </div>
        ))}
        
        <button type="button" className="add-variant-button" onClick={addVariant}>Add Another Variant</button>
        
        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Saving...' : 'Add Product'}
        </button>
      </form>
    </div>
  );
}

export default AddGroceryProduct;